import React from 'react';
import { useAppDispatch } from '@/hooks/redux';
import { register } from '@/store/slices/authSlice';
import { showModal } from '@/store/slices/modalSlice';
import useVerificationCodeLogin from '@/features/auth/hooks/useVerificationCodeLogin';

type VerificationMode = 'verification' | 'login';

export type DownloadEmailSignupResult = 'registered' | 'verification';

export const DOWNLOAD_EMAIL_REGEX =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const isValidDownloadEmail = (value: string) =>
  DOWNLOAD_EMAIL_REGEX.test(value);

export const getDownloadSignupErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An error occurred';
};

const isEmailAlreadyInUseError = (error: unknown) =>
  /email.*already.*in use|email.*exists|already in use/i.test(
    getDownloadSignupErrorMessage(error)
  );

const generatePassword = () =>
  Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-10);

const getVerificationMode = ({
  reason,
  verified,
}: {
  reason?: string;
  verified?: boolean;
}): VerificationMode =>
  verified || reason === 'EMAIL_ALREADY_VERIFIED' ? 'login' : 'verification';

export const useDownloadEmailSignup = () => {
  const dispatch = useAppDispatch();
  const { checkEmail, sendCode, sendLoginCode } = useVerificationCodeLogin();

  const openVerification = React.useCallback(
    async (
      normalizedEmail: string,
      verificationMode: VerificationMode,
      modalData: Record<string, unknown> | undefined
    ) => {
      const sendResult =
        verificationMode === 'login'
          ? await sendLoginCode(normalizedEmail)
          : await sendCode(normalizedEmail);

      dispatch(
        showModal({
          modalKey: 'DownloadVerificationCode',
          modalData: {
            ...modalData,
            email: normalizedEmail,
            initialResendDelay: sendResult.retryAfterSeconds ?? 60,
            verificationMode,
          },
        })
      );
    },
    [dispatch, sendCode, sendLoginCode]
  );

  const submitEmail = React.useCallback(
    async (
      normalizedEmail: string,
      modalData?: Record<string, unknown>
    ): Promise<DownloadEmailSignupResult> => {
      try {
        const checkResult = await checkEmail(normalizedEmail);

        if (checkResult.exists) {
          await openVerification(
            normalizedEmail,
            getVerificationMode(checkResult),
            modalData
          );
          return 'verification';
        }

        await dispatch(
          register({
            email: normalizedEmail,
            password: generatePassword(),
            displayName: 'User',
            isSilent: true,
          })
        ).unwrap();

        return 'registered';
      } catch (signupError) {
        if (!isEmailAlreadyInUseError(signupError)) {
          throw signupError;
        }

        const checkResult = await checkEmail(normalizedEmail);
        await openVerification(
          normalizedEmail,
          getVerificationMode(checkResult),
          modalData
        );
        return 'verification';
      }
    },
    [checkEmail, dispatch, openVerification]
  );

  return { submitEmail };
};
